import type { AttachmentsResource } from '@tadpoles/api-client'
import type { Attachment } from '@tadpoles/common'

import type { FamilyUserLocalBacking } from './persistence/family-local-backing.js'
import type { CachedAttachment } from './persistence/stores/attachment-store.js'
import type { FamilyAttachmentStore } from './persistence/stores/attachment-store.js'
import type { ServiceLifecycle } from './service-lifecycle.js'
import { UserScopedService, type UserScopedServiceScope } from './user-scoped-service-scope.js'

export type AttachmentServiceOptions = {
    createObjectUrl?: (data: Blob) => string
    isOnline?: () => boolean
    now?: () => number
    revokeObjectUrl?: (url: string) => void
    validationMaxAgeMs?: number
}

export type AttachmentAssetState =
    | { status: 'loading' }
    | { status: 'ready'; url: string }
    | { status: 'offline' }
    | { status: 'unavailable' }

export interface AttachmentAsset {
    readonly state: AttachmentAssetState
}

type LoadedAttachmentData = {
    cached?: CachedAttachment
    data: Blob
}

export type AttachmentPreviewRendition = 'thumbnail' | 'enail'

type AttachmentRepresentation = 'data' | AttachmentPreviewRendition

class OfflineAttachmentError extends Error {}

class ManagedAttachmentAsset implements AttachmentAsset {
    #state = $state.raw<AttachmentAssetState>({ status: 'loading' })

    public get state(): AttachmentAssetState {
        return this.#state
    }

    public transition(state: AttachmentAssetState): void {
        this.#state = state
    }
}

export class AttachmentService extends UserScopedService implements ServiceLifecycle {
    static readonly #DEFAULT_VALIDATION_MAX_AGE_MS = 24 * 60 * 60 * 1000

    readonly #api: AttachmentsResource
    readonly #createObjectUrl: (data: Blob) => string
    readonly #inFlight = new Map<string, Promise<LoadedAttachmentData>>()
    readonly #isOnline: () => boolean
    readonly #localBacking: FamilyUserLocalBacking
    readonly #now: () => number
    readonly #objectUrls = new Map<string, string>()
    readonly #operationController = new AbortController()
    readonly #retiredObjectUrls = new Set<string>()
    readonly #revokeObjectUrl: (url: string) => void
    readonly #previewAssets = new Map<string, ManagedAttachmentAsset>()
    readonly #validationInFlight = new Map<string, Promise<void>>()
    readonly #validationMaxAgeMs: number
    #destroyed = false
    #store: FamilyAttachmentStore | undefined

    public constructor(
        scope: UserScopedServiceScope,
        api: AttachmentsResource,
        localBacking: FamilyUserLocalBacking,
        options: AttachmentServiceOptions = {}
    ) {
        super(scope)
        this.#api = api
        this.#localBacking = localBacking
        this.#createObjectUrl = options.createObjectUrl ?? URL.createObjectURL
        this.#isOnline =
            options.isOnline ??
            (() => typeof navigator === 'undefined' || navigator.onLine !== false)
        this.#now = options.now ?? Date.now
        this.#revokeObjectUrl = options.revokeObjectUrl ?? URL.revokeObjectURL
        this.#validationMaxAgeMs =
            options.validationMaxAgeMs ?? AttachmentService.#DEFAULT_VALIDATION_MAX_AGE_MS
    }

    public async initialize(): Promise<void> {
        this.#assertAvailable()
        try {
            this.#store = await this.#localBacking.getAttachmentStore()
        } catch (error) {
            console.warn('Unable to initialize attachment caching.', error)
            this.#store = undefined
        }
    }

    public resume(): void {}

    public suspend(): void {}

    public destroy(): void {
        if (this.#destroyed) {
            return
        }

        this.#destroyed = true
        this.#operationController.abort()
        this.#inFlight.clear()
        this.#previewAssets.clear()
        this.#validationInFlight.clear()
        for (const url of [...this.#objectUrls.values(), ...this.#retiredObjectUrls]) {
            this.#revokeObjectUrl(url)
        }
        this.#objectUrls.clear()
        this.#retiredObjectUrls.clear()
        this.#store = undefined
    }

    public async getUrl(attachment: Attachment): Promise<string> {
        this.#assertAvailable()
        const attachmentKey = this.#getRemoteKey(attachment)

        if (attachment.mimeType.toLowerCase().startsWith('video/')) {
            return this.#api.getUrl(attachmentKey)
        }

        return await this.#getBlobUrl(attachmentKey, 'data')
    }

    public getEnail(attachment: Attachment): AttachmentAsset {
        return this.#getPreview(attachment, 'enail')
    }

    public getThumbnail(attachment: Attachment): AttachmentAsset {
        return this.#getPreview(attachment, 'thumbnail')
    }

    public prefetchEnail(attachment: Attachment): void {
        this.#assertAvailable()
        this.getEnail(attachment)
    }

    public prefetchThumbnail(attachment: Attachment): void {
        this.#assertAvailable()
        this.getThumbnail(attachment)
    }

    #getPreview(
        attachment: Attachment,
        representation: AttachmentPreviewRendition
    ): AttachmentAsset {
        this.#assertAvailable()
        let attachmentKey: string
        try {
            attachmentKey = this.#getRemoteKey(attachment)
        } catch {
            const asset = new ManagedAttachmentAsset()
            asset.transition({ status: 'unavailable' })
            return asset
        }

        const cacheKey = this.#getCacheKey(attachmentKey, representation)
        const existingAsset = this.#previewAssets.get(cacheKey)
        if (existingAsset) {
            return existingAsset
        }

        const asset = new ManagedAttachmentAsset()
        this.#previewAssets.set(cacheKey, asset)
        void this.#resolvePreview(asset, attachmentKey, representation, cacheKey)
        return asset
    }

    async #resolvePreview(
        asset: ManagedAttachmentAsset,
        attachmentKey: string,
        representation: AttachmentPreviewRendition,
        cacheKey: string
    ): Promise<void> {
        try {
            const url = await this.#getBlobUrl(attachmentKey, representation, cacheKey)
            if (!this.#operationController.signal.aborted) {
                asset.transition({ status: 'ready', url })
            }
        } catch (error) {
            if (!this.#operationController.signal.aborted) {
                asset.transition({
                    status:
                        error instanceof OfflineAttachmentError || !this.#isOnline()
                            ? 'offline'
                            : 'unavailable'
                })
            }
        } finally {
            if (this.#previewAssets.get(cacheKey) === asset) {
                this.#previewAssets.delete(cacheKey)
            }
        }
    }

    async #getBlobUrl(
        attachmentKey: string,
        representation: AttachmentRepresentation,
        resolvedCacheKey?: string
    ): Promise<string> {
        // Use the stable application URL as cache identity, never its expiring redirect target.
        const cacheKey = resolvedCacheKey ?? this.#getCacheKey(attachmentKey, representation)
        const existingUrl = this.#objectUrls.get(cacheKey)
        if (existingUrl) {
            this.#scheduleValidation(attachmentKey, representation, cacheKey)
            return existingUrl
        }

        const loaded = await this.#getData(attachmentKey, representation, cacheKey)
        this.#assertAvailable()
        const resolvedUrl = this.#objectUrls.get(cacheKey)
        if (resolvedUrl) {
            return resolvedUrl
        }
        const url = this.#createObjectUrl(loaded.data)
        this.#objectUrls.set(cacheKey, url)
        if (loaded.cached) {
            this.#scheduleValidation(attachmentKey, representation, cacheKey, loaded.cached)
        }
        return url
    }

    #getData(
        attachmentKey: string,
        representation: AttachmentRepresentation,
        cacheKey: string
    ): Promise<LoadedAttachmentData> {
        const existingRequest = this.#inFlight.get(cacheKey)
        if (existingRequest) {
            return existingRequest
        }

        const request = this.#loadData(attachmentKey, representation, cacheKey).finally(() => {
            this.#inFlight.delete(cacheKey)
        })
        this.#inFlight.set(cacheKey, request)
        return request
    }

    async #loadData(
        attachmentKey: string,
        representation: AttachmentRepresentation,
        cacheKey: string
    ): Promise<LoadedAttachmentData> {
        try {
            const cached = await this.#store?.get(cacheKey)
            if (cached) {
                return { cached, data: cached.data }
            }
        } catch (error) {
            console.warn('Unable to read cached attachment data.', error)
        }

        if (representation !== 'data' && !this.#isOnline()) {
            throw new OfflineAttachmentError()
        }

        const requestOptions = { signal: this.#operationController.signal }
        const data =
            representation === 'thumbnail'
                ? await this.#api.getThumbnail(attachmentKey, requestOptions)
                : representation === 'enail'
                  ? await this.#api.getEnail(attachmentKey, requestOptions)
                  : await this.#api.getData(attachmentKey, requestOptions)

        try {
            if (!this.#operationController.signal.aborted) {
                await this.#store?.put(cacheKey, data, this.#now())
            }
        } catch (error) {
            console.warn('Unable to cache attachment data.', error)
        }

        return { data }
    }

    #scheduleValidation(
        attachmentKey: string,
        representation: AttachmentRepresentation,
        cacheKey: string,
        cached?: CachedAttachment
    ): void {
        if (!this.#store || !this.#isOnline() || this.#validationInFlight.has(cacheKey)) {
            return
        }

        // Cached data has already been returned; validation is intentionally request-driven.
        const validation = this.#validateIfStale(
            attachmentKey,
            representation,
            cacheKey,
            cached?.metadata
        ).finally(() => {
            this.#validationInFlight.delete(cacheKey)
        })
        this.#validationInFlight.set(cacheKey, validation)
    }

    async #validateIfStale(
        attachmentKey: string,
        representation: AttachmentRepresentation,
        cacheKey: string,
        knownMetadata?: CachedAttachment['metadata']
    ): Promise<void> {
        const store = this.#store
        if (!store) {
            return
        }

        try {
            const metadata = knownMetadata ?? (await store.getMetadata(cacheKey))
            if (metadata && this.#now() - metadata.lastValidatedAt < this.#validationMaxAgeMs) {
                return
            }

            const validationOptions = { signal: this.#operationController.signal }
            const existence =
                representation === 'thumbnail'
                    ? await this.#api.validateThumbnail(attachmentKey, validationOptions)
                    : representation === 'enail'
                      ? await this.#api.validateEnail(attachmentKey, validationOptions)
                      : await this.#api.validateData(attachmentKey, validationOptions)

            if (this.#operationController.signal.aborted) {
                return
            }

            if (existence === 'exists') {
                await store.recordValidation(cacheKey, this.#now())
                return
            }

            if (existence !== 'missing') {
                return
            }

            // Keep serving the existing object URL until physical eviction succeeds. A failed
            // deletion leaves the cached entry available for a later validation attempt.
            await store.delete(cacheKey)
            const staleObjectUrl = this.#objectUrls.get(cacheKey)
            if (staleObjectUrl) {
                this.#objectUrls.delete(cacheKey)
                // Keep the URL alive for the stale read already handed to presentation; future
                // reads cannot obtain it, and the service lifecycle revokes it deterministically.
                this.#retiredObjectUrls.add(staleObjectUrl)
            }
        } catch (error) {
            if (!this.#operationController.signal.aborted) {
                console.warn('Unable to validate cached attachment data.', error)
            }
        }
    }

    #getRemoteKey(attachment: Attachment): string {
        const attachmentKey =
            !attachment.key || this.#isLocalKey(attachment.key)
                ? attachment.originKey
                : attachment.key
        if (!attachmentKey || this.#isLocalKey(attachmentKey)) {
            throw new Error('Attachment data requires a remote attachment key.')
        }

        return attachmentKey
    }

    #getCacheKey(attachmentKey: string, representation: AttachmentRepresentation): string {
        if (representation === 'thumbnail') {
            return this.#api.getThumbnailUrl(attachmentKey)
        }

        if (representation === 'enail') {
            return this.#api.getEnailUrl(attachmentKey)
        }

        return this.#api.getUrl(attachmentKey)
    }

    #isLocalKey(key: string): boolean {
        return key.startsWith('L.')
    }

    #assertAvailable(): void {
        if (this.#destroyed) {
            throw new Error('Cannot use a destroyed attachment service.')
        }
    }
}
